import {
  brandsByModel,
  gripSizeByHandle,
  handlesByBrand,
  handlesByModel,
  pipesByModelAndGripSize,
  rosettaSpheresAttributesByCustomCategory,
  sportByModel,
} from '../data/data';

export const shouldShowGripSizeStep = (gripSize: string): boolean => {
  return !['Non ha taglia', 'Taglia unica', 'Taglia su richiesta'].includes(
    gripSize,
  );
};

export function getCompatibleHandles(
  productCode: string,
  brand: string,
): string[] {
  const modelHandles = handlesByModel[productCode] || [];
  const brandHandles = handlesByBrand[brand] || [];

  // Restituisce solo le impugnature presenti in entrambi gli array
  return modelHandles.filter((handle) => brandHandles.includes(handle));
}

export const getAvailableBrand = (productCode: string): string[] => {
  return brandsByModel[productCode] ?? [];
};

export const getAvailableGripSizes = (handle: string): string[] => {
  return gripSizeByHandle[handle] ?? [];
};

export const getAvailableHandlesByProduct = (productCode: string): string[] => {
  return handlesByModel[productCode] ?? [];
};

/*
export const getAvailablePipesByProduct = (productCode: string): string[] => {
  return pipesByModel[productCode] ?? [];
};*/

export const getAvailableSportByProduct = (productCode: string): string[] => {
  return sportByModel[productCode] ?? [];
};

export const isDefaultCategory = (name: string | undefined): boolean => {
  if (!name) return false;
  return ![
    'Combs',
    'Butt Plates',
    'Plates',
    'Handles',
    'Rosetta/Spheres',
    'Pipes',
  ].includes(name);
};

export const getCategoryForOption = (optionId: string): string | null => {
  if (rosettaSpheresAttributesByCustomCategory.JPN.includes(optionId)) {
    return 'JPN';
  } else if (rosettaSpheresAttributesByCustomCategory.STD.includes(optionId)) {
    return 'STD';
  }

  return null;
};

export const getAvailablePipesBySportAndGrip = (
  productCode: string,
  gripSize: string,
): string[] => {
  return pipesByModelAndGripSize[productCode]?.[gripSize] || [];
};

export const getAutomaticCombs = (
  sport: string | null,
  pipeSize: string | null,
): string[] => {
  if (!sport) return [];

  let combCodes: string[] = [];

  if (sport === 'Trap') {
    combCodes = ['W32'];
  } else if (sport === 'Skeet' || sport === 'Sporting') {
    combCodes = ['W24'];
  } else if (sport === 'Double trap') {
    combCodes = ['W24'];
  }

  if (pipeSize && pipeSize.includes('180 mm')) {
    if (sport === 'Trap') {
      combCodes = ['W32 L'];
    } else if (
      sport === 'Skeet' ||
      sport === 'Sporting' ||
      sport === 'Double trap'
    ) {
      combCodes = ['W24 L'];
    }
  }

  return combCodes;
};

// Funzione per generare il codice prodotto
export const generateProductCode = (
  attributes: Record<string, { name: string; quantity: number }>,
  othersInfo: {
    sport: string;
    brand: string;
    handle: string;
    gripSize: string;
    gripType: string;
    pipeSize: string;
  },
): string => {
  const positions = [4, 2, 5, 6];
  const cartKeys = Object.keys(attributes);

  const orderedNames = positions
    .filter((pos) => pos < cartKeys.length)
    .map((pos) => cartKeys[pos]);

  if (orderedNames.length < 4) {
    const remainingKeys = cartKeys.filter((key) => !orderedNames.includes(key));
    orderedNames.push(...remainingKeys.slice(0, 4 - orderedNames.length));
  }

  const bridge = cartKeys[1] || cartKeys[0];

  let codeBrand = '';

  switch (othersInfo.brand) {
    case 'Blaser F3':
      codeBrand = 'F3';
      break;
    case 'Browning 525: XT, GTI, 325, 425':
      codeBrand = 'BRW525';
      break;
    case 'Browning 725':
      codeBrand = 'BRW725';
      break;
    case 'Caesar Guerini Summit':
      codeBrand = 'SUMMIT';
      break;
    case 'P. Beretta ASE 90':
      codeBrand = 'DT11';
      break;
    case 'P. Beretta DT11: DT10':
      codeBrand = 'DT11';
      break;
    case 'P. Beretta 694: 680, 682, 686, 687, 688, 690, 692':
      codeBrand = '694';
      break;
    case 'Perazzi MX8, MX2000, HT':
      codeBrand = 'HT';
      break;
    case 'Kriegoff K80':
      codeBrand = 'K80';
      break;
    case 'Rizzini BR 440, BR 460 (Removable Trigger)':
      codeBrand = 'BR460';
      break;
    case 'Rizzini Vertex, BR110 (No Removable Trigger)':
      codeBrand = 'BR110';
      break;
    case 'A. Zoli Kronos: Z Gun':
      codeBrand = 'KRONOS';
      break;
    case 'Beretta A400':
      codeBrand = 'A400';
      break;
    default:
      codeBrand = 'UNKNOWN';
  }

  const firstKey = cartKeys[0];
  const specialGripCodes = ['C-HANDLE', 'ANATOMGRIP'];

  const gripValue = specialGripCodes.includes(firstKey)
    ? firstKey
    : othersInfo.gripSize;

  const handedness =
    othersInfo.gripType === 'Sinistro' || othersInfo.gripType === 'Left-handed'
      ? 'SX'
      : 'DX';

  return `${othersInfo.sport} ${bridge} - ${codeBrand} ${gripValue} ${handedness} ${orderedNames.join(' ')}`;
};
